import { Injectable } from '@angular/core';
import { State } from '@app/store';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  take,
} from 'rxjs';
import {
  RequestCreditAttachment,
  SousStatus,
  SupportRequest,
  SupportRequestAttachment,
} from '@usf/list-types';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import {
  selectSupportCenterProductsToLoad,
  selectSupportCenterViewModel,
} from '../selectors/support-center-selectors';
import { SortableSectionHeader } from '@shared/components/sortable-section/models/sortable-section.model';
import { ModalService } from '@shared/services/modal/modal.service';
import { ModalController } from '@ionic/angular';
import { SupportCenterModalComponent } from '../components/support-center-modal/support-center-modal.component';
import {
  SERVICE_REQUEST_ACTIONS,
  ServiceRequestFacadeService,
  SUPPORT_CENTER_ACTIONS,
} from '@usf/ngrx-list';
import {
  SupportCenterContentViewModel,
  SupportCenterCardClick,
  SupportCenterPageViewModel,
  SupportRequestModalData,
} from '../model/support-center-model';
import { PhotoService } from '@shared/services/photo/photo.service';
import { UserPhoto } from '@shared/services/photo/model/photo.model';
import { ServiceRequestService } from '@service-request/services/service-request.service';
import { FileService } from '@shared/services/file/file.service';

@Injectable({
  providedIn: 'root',
})
export class SupportCenterService {
  supportRequestModalIsOpen$ = new BehaviorSubject(false);
  supportCenterModalOptions$ = new BehaviorSubject<SupportCenterCardClick>({
    isOpen: false,
    supportItem: {} as SupportCenterContentViewModel,
  });
  private supportRequestPhotosSubject = new BehaviorSubject<UserPhoto[]>([]);
  supportRequestPhotos$: Observable<UserPhoto[]>;
  email$ = this.panAppState.user$.pipe(map(user => user.email));

  constructor(
    public store: Store<State>,
    private modalService: ModalService,
    private platformService: PlatformService,
    private modalController: ModalController,
    private photoService: PhotoService,
    private serviceRequestService: ServiceRequestService,
    private panAppState: PanAppState,
    private serviceRequestFacadeService: ServiceRequestFacadeService,
    private fileService: FileService,
  ) {
    this.supportRequestPhotos$ =
      this.supportRequestPhotosSubject.asObservable();
  }

  loadSupportCenterContent = () => {
    this.store.dispatch(SUPPORT_CENTER_ACTIONS.load());
  };

  resetSupportCenterContent = () => {
    this.store.dispatch(SUPPORT_CENTER_ACTIONS.resetStore());
  };

  getProductsToLoad$ = () => {
    return this.store.select(selectSupportCenterProductsToLoad());
  };

  serviceViewModel$ = (
    platform: PlatformEnum,
  ): Observable<SupportCenterPageViewModel> => {
    return this.store.select(selectSupportCenterViewModel(platform));
  };

  updateFilter = (status: SousStatus) => {
    this.store.dispatch(
      SUPPORT_CENTER_ACTIONS.updateFilter({ filter: status }),
    );
  };

  updateSort = (sort: SortableSectionHeader) => {
    this.store.dispatch(
      SUPPORT_CENTER_ACTIONS.updateSort({
        selectedHeader: sort.headerName,
        sortDirection: sort.sortDirection,
      }),
    );
  };

  requestResubmit = (primaryKey: string) => {
    this.store.dispatch(SERVICE_REQUEST_ACTIONS.resubmit({ primaryKey }));
  };

  showSupportCenterModal = (options: SupportCenterCardClick) => {
    this.supportCenterModalOptions$.next(options);
  };

  closeSupportCenterModal() {
    this.supportCenterModalOptions$.next({
      ...this.supportCenterModalOptions$.value,
      isOpen: false,
    });
  }

  showSupportRequestModal = (isOpen: boolean) => {
    this.supportRequestModalIsOpen$.next(isOpen);
  };

  async dismissModal() {
    await this.modalController?.dismiss();
  }

  async onAddPhoto() {
    try {
      let photo = await this.photoService.addPhoto();
      let photos = [];
      if (this.supportRequestPhotosSubject.value) {
        photos = Array.from(this.supportRequestPhotosSubject.value);
      }

      photos.push(photo);
      this.supportRequestPhotosSubject.next(photos);
    } catch (error) {
      this.serviceRequestService.handleFileErrors(error.message);
    }
  }

  async onDeletePhoto(photo: UserPhoto) {
    this.photoService.deletePhoto(photo);
    this.supportRequestPhotosSubject.next(
      this.supportRequestPhotosSubject.value.filter(value => value !== photo),
    );
  }

  getSupportRequestIssues$() {
    return this.serviceRequestFacadeService.supportRequestIssueOptions$();
  }

  submitSupportRequest = async (modalData: SupportRequestModalData) => {
    const supportRequest = await this.transformSupportRequest(modalData);
    this.store.dispatch(
      SUPPORT_CENTER_ACTIONS.submitSupportRequest({ supportRequest }),
    );
    this.photoService.deletePhotos(this.supportRequestPhotosSubject.value);
    this.supportRequestPhotosSubject.next([]);
  };

  transformSupportRequest = async (modalData: SupportRequestModalData) => {
    const attachments = await this.convertFilesToAttachments(
      modalData.file,
      this.supportRequestPhotosSubject.value,
    );
    const [customerNumber, divisionNumber, customerName, recId] =
      await firstValueFrom(
        combineLatest([
          this.panAppState.customer$,
          this.serviceRequestFacadeService.serviceRequest$(),
        ]).pipe(
          take(1),
          map(([customer, serviceRequestState]) => {
            return [
              customer.customerNumber,
              customer.divisionNumber,
              customer.customerName,
              serviceRequestState.serviceRequest.supportRequest.recordTypeId,
            ];
          }),
        ),
      );

    let supportRequest: SupportRequest = {
      subject: 'Initiated by Moxe',
      desc: modalData.comments,
      source:
        this.platformService.platformType ===
        this.platformService.platformEnum.desktop
          ? 'Moxe Desktop'
          : 'Moxe Mobile',
      tandemAcntNbr: customerNumber,
      divisionNumber: divisionNumber,
      origin: 'Web',
      webName: customerName,
      webEmail: modalData.email,
      caseStatus: 'New',
      caseType: 'Moxe Requests',
      recTypeId: recId,
      servReqRsn: modalData.requestReason.reasonCode,
      files: attachments.fileArray,
      images: attachments.photoArray,
    };

    return supportRequest;
  };

  convertFilesToAttachments = async (file: File, photos: UserPhoto[]) => {
    const photoArray: SupportRequestAttachment[] = [];
    const fileArray: SupportRequestAttachment[] = [];
    if (photos.length > 0) {
      for await (const photo of photos) {
        try {
          const photoValue = await this.fileService.loadFileData(
            photo.filepath,
          );
          const attachment = {
            name: photo.filepath,
            type: photo.extension,
            value: photoValue,
          };
          photoArray.push(attachment); // happening before attachment exists
        } catch (error) {}
      }
    }

    if (!!file) {
      const fileValue = await this.fileService.convertFileToBase64String(file);
      const fileAttachment: SupportRequestAttachment = {
        name: file.name.replace(/\.[^.]+$/, ''),
        type: this.getFileExtension(file.name),
        value: fileValue,
      };
      fileArray.push(fileAttachment);
    }
    return { photoArray, fileArray };
  };

  getFileExtension = (fileName: string) => {
    return fileName.split('.').pop();
  };
}
